import { FC, ReactNode } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { FieldWrapper, FormInput, RecurrenceField } from "../../../shared/ui";
import { FormSelect } from "../../../shared/ui/controls/FormSelect";
import { AttachmentsSection } from "../../../shared/ui/controls/AttachmentsSection/AttachmentsSection";
import { SegmentedControl } from "../../../shared/ui/controls/SegmentedControl";
import Checkbox from "../../../shared/ui/controls/Checkbox/Checkbox";
import { HandwerkerSearchSelect } from "../../../entities/user";
import { GewerkSearchSelect } from "../../../entities/gewerk";
import { appointmentStatusOptions } from "../../../entities/appointment";
import {
  hasActuals,
  type AppointmentFormValues,
} from "../model/appointmentForm.schema";

/**
 * Fields only — no queries, no mutations. Everything it needs arrives as props from
 * `UpsertAppointmentModal`, which owns the data and the submit.
 *
 * Ordered by decision, not by data model: who and when comes first because that is what the user
 * opened the dialog to choose. Description and attachments are optional context and sit last.
 * `FormSection` is deliberately not used here — its white card inside a white modal was a
 * container with nothing to contain; grouping is proximity plus a hairline instead.
 */

const SERIES_SCOPE_ITEMS = [
  { value: "single" as const, label: "Nur dieser Termin" },
  { value: "series" as const, label: "Dieser und alle folgenden" },
];

const Section: FC<{ title: string; hint?: string; children: ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <section className="flex flex-col gap-4 border-t border-slate-200 pt-6 first:border-0 first:pt-0">
    <div className="flex flex-col gap-0.5">
      <h3 className="font-['Roboto'] text-[15px] font-bold leading-none text-[#124093]">
        {title}
      </h3>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
    {children}
  </section>
);

interface Props {
  control: Control<AppointmentFormValues>;
  mode: "create" | "edit";
  /**
   * True whenever the work order is already known — from the page, the URL or the appointment
   * being edited. The Auftrag then lives in the modal subtitle instead, because it identifies what
   * is being scheduled rather than being something this form decides.
   *
   * The field below is what is left when it is false: a create with no work order at all, which
   * only a hand-typed `/disposition?disposition-mode=schedule-appointment` produces. `workOrderId`
   * is required by the schema, so without the input that URL would be an unsubmittable dead end.
   */
  workOrderLocked: boolean;
  /** Edit mode: the appointment belongs to a series, so the scope toggle applies. */
  hasSeries: boolean;
  /** Current `plannedStart` — the anchor the recurrence preview is derived from. */
  plannedStart: string;
  /**
   * Why Ganztägig did nothing, when it did nothing. Resolved by the modal, which owns the query —
   * this component stays free of both queries and mutations.
   */
  fullDayHint?: string;
  files: File[];
  onFilesChange: (files: File[]) => void;
  existingAttachments: string[];
  onRemoveExistingAttachment: (index: number) => void;
}

export const AppointmentFormFields: FC<Props> = ({
  control,
  mode,
  workOrderLocked,
  hasSeries,
  plannedStart,
  fullDayHint,
  files,
  onFilesChange,
  existingAttachments,
  onRemoveExistingAttachment,
}) => {
  /**
   * Read from the form rather than from the loaded appointment: the status is editable, so the
   * Ist-Zeiten have to appear in the same render as the user picking "Abgeschlossen". The mapper
   * gates on the same value — see `hasActuals`.
   */
  const status = useWatch({ control, name: "status" });
  const showActuals = hasActuals(status);

  return (
    <div className="flex w-full flex-col gap-6">
      {/*
      One 6-column grid for the whole section rather than a 2-col row stacked on a 3-col row:
      6 divides by both, so the halves and the thirds share vertical edges instead of each row
      inventing its own gutter positions.
    */}
      <Section title="Termin">
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
          {!workOrderLocked && (
            <FieldWrapper label="Auftrag" required className="col-span-2 sm:col-span-3">
              <FormInput name="workOrderId" control={control} as="input" />
            </FieldWrapper>
          )}

          <FieldWrapper label="Handwerker" required className="col-span-2 sm:col-span-3">
            <Controller
              name="assignedTechnicianId"
              control={control}
              render={({ field, fieldState }) => (
                <HandwerkerSearchSelect
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  error={fieldState.error?.message ?? null}
                  inputRef={field.ref}
                  placeholder="Handwerker suchen..."
                />
              )}
            />
          </FieldWrapper>

          {/*
          Edit only — a new Termin's status is the backend's. Placed here rather than at the top of
          the section so it pairs with Handwerker on one row: with the Auftrag field present the
          halves read `Auftrag | Handwerker` and `Status | Termin um`, and when it is locked the one
          unavoidable gap lands beside the taller Termin cell instead of on its own line.

          All five states are offered, EXPIRED included. Picking a completed one reveals the
          Ist-Zeiten below and needs a Manager rolle — the API answers 403 otherwise.
        */}
          {mode === "edit" && (
            <FieldWrapper label="Status" className="col-span-2 sm:col-span-3">
              <FormSelect
                name="status"
                control={control}
                options={appointmentStatusOptions}
              />
            </FieldWrapper>
          )}

          {/*
          Ganztägig sits inside the time cell, not on its own row: it modifies the time rather
          than standing beside it, and as a separate grid item it backfilled the hole left by the
          2-wide duration cells and made the row ragged.
        */}
          <FieldWrapper label="Termin um" required className="col-span-2 sm:col-span-3">
            <FormInput
              name="plannedStart"
              control={control}
              type="datetime-local"
              as="input"
            />
            <div className="mt-2">
              <Controller
                name="isFullDay"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={field.onChange}
                    label="Ganztägig"
                  />
                )}
              />
              {/*
                Ganztägig fills the Schicht: the three durations below are rewritten so the belegter
                Block runs shiftStart–shiftEnd. They stay editable, and editing one clears the
                checkbox again — see `useFullDaySync`.
              */}
              {fullDayHint && (
                <p className="mt-1 text-xs text-amber-600">{fullDayHint}</p>
              )}
            </div>
          </FieldWrapper>
          { }
          <FieldWrapper label="Anfahrt (min)" className="sm:col-span-2 sm:col-start-1">
            <FormInput
              name="plannedTravelDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              min={0}
              valueAsNumber
            />
          </FieldWrapper>
          <FieldWrapper label="Arbeitszeit (min)" required className="sm:col-span-2">
            <FormInput
              name="plannedWorkDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              min={0}
              valueAsNumber
            />
          </FieldWrapper>
          <FieldWrapper label="Abfahrt (min)" className="sm:col-span-2">
            <FormInput
              name="plannedDepartureDuration"
              control={control}
              type="number"
              as="input"
              step={15}
              min={0}
              valueAsNumber
            />
          </FieldWrapper>
          {/*
          Ist-Zeiten, only for a completed appointment. Same columns directly under their planned
          counterparts so a correction is read as a comparison, not as a new set of fields.
          `hasActuals` also gates the submit mapper — the two must not drift apart.

          These four are what the calendar draws once the appointment is completed: occupancy is
          `COALESCE(actual, planned)`, so a drag on the board retypes *these* inputs and not the
          planned ones above. Which is why the Ist start is an ordinary visible field rather than
          something only a drag can reach.
        */}
          {showActuals && (
            <>
              <FieldWrapper
                label="Ist-Termin um"
                className="col-span-2 sm:col-span-3 sm:col-start-1"
              >
                <FormInput
                  name="actualStart"
                  control={control}
                  type="datetime-local"
                  as="input"
                />
              </FieldWrapper>
              {/*
                Ist-Ende is a reported sign-off, not a piece of the block — it sits outside the
                occupancy CHECK and moves nothing. `useActualEndSync` keeps it trailing the Ist
                start and Arbeitszeit until somebody types their own value here, after which it is
                left alone. Left empty on a completed Termin it would go on reading as *In Arbeit*.
              */}
              <FieldWrapper label="Ist-Ende" className="col-span-2 sm:col-span-3">
                <FormInput
                  name="actualEnd"
                  control={control}
                  type="datetime-local"
                  as="input"
                />
              </FieldWrapper>
              <FieldWrapper
                label="Ist-Anfahrt (min)"
                className="sm:col-span-2 sm:col-start-1"
              >
                <FormInput
                  name="actualTravelDuration"
                  control={control}
                  type="number"
                  as="input"
                  step={15}
                  min={0}
                  valueAsNumber
                />
              </FieldWrapper>
              <FieldWrapper label="Ist-Arbeitszeit (min)" className="sm:col-span-2">
                <FormInput
                  name="actualWorkDuration"
                  control={control}
                  type="number"
                  as="input"
                  step={15}
                  min={0}
                  valueAsNumber
                />
              </FieldWrapper>
              <FieldWrapper label="Ist-Abfahrt (min)" className="sm:col-span-2">
                <FormInput
                  name="actualDepartureDuration"
                  control={control}
                  type="number"
                  as="input"
                  step={15}
                  min={0}
                  valueAsNumber
                />
              </FieldWrapper>
            </>
          )}
        </div>
      </Section>

      {/*
      Create offers the rule; edit offers only the scope, because the frequency of an existing
      series is not patchable. Neither variant carries a hint line — the checkbox label and the
      two segment labels already say what they do.
    */}
      {mode === "create" ? (
        <Section title="Serie">
          <Controller
            name="recurrence"
            control={control}
            render={({ field }) => (
              <RecurrenceField
                startDate={plannedStart}
                value={field.value}
                onChange={field.onChange}
                label="Als Serie wiederholen"
              />
            )}
          />
        </Section>
      ) : hasSeries ? (
        <Section title="Serie" hint="Die Wiederholung selbst lässt sich nicht ändern.">
          <Controller
            name="forFullSeries"
            control={control}
            render={({ field }) => (
              <SegmentedControl
                size="sm"
                className="self-start"
                items={SERIES_SCOPE_ITEMS}
                value={field.value ? "series" : "single"}
                onChange={(next) => field.onChange(next === "series")}
                ariaLabel="Änderungsumfang"
              />
            )}
          />
        </Section>
      ) : null}

      <Section title="Details">
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
          <FieldWrapper label="Gewerk" className="col-span-2 sm:col-span-3">
            <Controller
              name="tradeCode"
              control={control}
              render={({ field, fieldState }) => (
                <GewerkSearchSelect
                  value={field.value}
                  // Clearing the input emits null; `tradeCode` is a required number where 0 is the
                  // API's "kein Gewerk", so land there rather than sending null through coercion.
                  onChange={(id) => field.onChange(id ?? 0)}
                  error={fieldState.error?.message ?? null}
                  inputRef={field.ref}
                />
              )}
            />
          </FieldWrapper>
        </div>

        <FieldWrapper label="Beschreibung">
          <FormInput name="description" control={control} as="textarea" rows={3} />
        </FieldWrapper>

        <AttachmentsSection
          existingAttachments={existingAttachments}
          onRemoveExistingAttachment={onRemoveExistingAttachment}
          files={files}
          onFilesChange={onFilesChange}
        />
      </Section>
    </div>
  )
}
